import { useMemo } from 'react';

import { EMPTY_ARRAY } from '@e-socna/empty';

import type { ThirdPartiesCmp_SomePerson } from '../queries/some-people.query';

import { useFindAllEnabledInternalPeopleQuery } from './useFindAllEnabledInternalPeopleQuery.hook';
import { useFindAllDisabledInternalPeopleQuery } from './useFindAllDisabledInternalPeopleQuery.hook';
import { useFindAllDeletedInternalPeopleQuery } from './useFindAllDeletedInternalPeopleQuery.hook';

interface UnionResult {
  readonly data:
    | Readonly<{ findSomePeople: readonly ThirdPartiesCmp_SomePerson[] }>
    | undefined;
  readonly isLoading: boolean;
  readonly isSuccess: boolean;
}

export const useFindAllInternalPeopleQuery = (enabled = true): UnionResult => {
  const deletedQuery = useFindAllDeletedInternalPeopleQuery(enabled);
  const disabledQuery = useFindAllDisabledInternalPeopleQuery(enabled);
  const enabledQuery = useFindAllEnabledInternalPeopleQuery(enabled);

  const memoizedResult = useMemo(() => {
    const deletedData = deletedQuery.data?.findSomePeople;
    const disabledData = disabledQuery.data?.findSomePeople;
    const enabledData = enabledQuery.data?.findSomePeople;

    const data =
      deletedData || disabledData || enabledData
        ? {
            findSomePeople: [
              ...(deletedData ?? EMPTY_ARRAY),
              ...(disabledData ?? EMPTY_ARRAY),
              ...(enabledData ?? EMPTY_ARRAY),
            ],
          }
        : undefined;

    const isLoading =
      deletedQuery.isLoading ||
      disabledQuery.isLoading ||
      enabledQuery.isLoading;
    const isSuccess =
      deletedQuery.isSuccess &&
      disabledQuery.isSuccess &&
      enabledQuery.isSuccess;

    return { data, isLoading, isSuccess };
  }, [
    deletedQuery.data?.findSomePeople,
    deletedQuery.isLoading,
    deletedQuery.isSuccess,
    disabledQuery.data?.findSomePeople,
    disabledQuery.isLoading,
    disabledQuery.isSuccess,
    enabledQuery.data?.findSomePeople,
    enabledQuery.isLoading,
    enabledQuery.isSuccess,
  ]);

  return memoizedResult;
};
